/**
 * @file `StateGoalScheduler` — rate-limited, coalescing evaluator for
 *       `{via:'state'}` detectors (#1149).
 * @module @labcraft/course-kit/runtime/state-scheduler
 *
 * `state` detectors never match the synchronous command-event path
 * (`matchDetector` stays pure over `{cmd, exit, cwd}` events) — they
 * are evaluated out-of-band by running the author-supplied `check`
 * script inside the guest via the platform's probe RPC (#1147). This
 * scheduler owns the evaluation policy so `<ChapterProvider/>` stays a
 * thin wire-up and the policy is unit-testable DOM-free:
 *
 *   - **Trigger** — the provider calls {@link StateGoalScheduler.evaluate}
 *     with the currently-relevant checks on every command event, and
 *     with `force: true` on an explicit reassert (reconnect / reboot
 *     survival — the machine came back to `running`, so goals the
 *     reboot itself satisfied re-probe and complete).
 *   - **Rate limit** — per unique `check` string, at most one probe per
 *     `minIntervalMs` (default 2000 ms). A command landing inside the
 *     window is skipped; the next command outside it re-probes.
 *     `force` bypasses the window (reassert must probe everything).
 *   - **Coalesce** — a trigger arriving while a probe for the same
 *     check is in flight marks it dirty; when the in-flight probe
 *     settles unsatisfied, exactly one follow-up runs. A satisfied
 *     settle drops the dirty bit — the completion already dispatched,
 *     so a follow-up would probe for nobody.
 *   - **Errors** — a rejected probe (wire drop, timeout, agent down) is
 *     "not satisfied": swallowed with a debug log, never a crash, never
 *     a gate. The next trigger simply probes again.
 *   - **Retry tick** — the scheduler re-evaluates the most recent
 *     trigger's still-unsatisfied checks every `retryIntervalMs`
 *     (default 10 s) on its own timer. Without it, a state-only beat
 *     (one command, whose single completion event races a transiently
 *     failing probe) would strand the learner with nothing left to type
 *     to re-trigger evaluation. Satisfied checks drop out of the retry
 *     set; a fresh trigger replaces it wholesale; the tick respects the
 *     per-check rate limit, so the effective cadence is
 *     `max(retryIntervalMs, minIntervalMs)`.
 *
 * On a satisfied probe the scheduler calls `onSatisfied(check)`; the
 * provider dispatches the SAME completion action the scanner path uses
 * (`objective.completed`) for the active objective whose `satisfiedBy`
 * carries that check.
 */

import type { ChapterState } from './reducer.ts'
import { scanWindow } from './scanner.ts'

/**
 * Source of out-of-band state-probe runs (#1149). The platform (lab)
 * adapts its terminal `probe` / `probe-result` frame round-trip to
 * this; a test fixture resolves synthetically. The implementation
 * decides how a raw probe outcome maps to `satisfied` (lab: exit 0).
 * Mirrors `CommandSource` / `MentorSource`'s seam role so the adapters
 * read identically at the mount site.
 */
export interface ProbeSource {
  /**
   * Run one author-trusted check script in the guest, out-of-band from
   * the learner's pty. Resolves `{satisfied}`; may reject on delivery
   * failure (the scheduler treats a rejection as not satisfied).
   *
   * @param check - The `{via:'state'}` detector's check script.
   * @returns Whether the check passed (exit 0).
   */
  runCheck(check: string): Promise<{ satisfied: boolean }>
}

/**
 * Static configuration for {@link StateGoalScheduler}.
 *
 * @property minIntervalMs - Minimum gap between two evaluations of the
 *   same check (rate limit). Defaults to 2000 ms; `<ChapterProvider/>`
 *   surfaces it as `probeIntervalMs`.
 * @property retryIntervalMs - Gap between self-driven re-evaluations of
 *   the latest trigger's still-unsatisfied checks (the retry tick).
 *   Defaults to 10 000 ms; `<ChapterProvider/>` surfaces it as
 *   `probeRetryMs`. `0` disables the tick (tests that drive triggers
 *   explicitly).
 */
export type StateGoalSchedulerProps = {
  minIntervalMs?: number
  retryIntervalMs?: number
}

/**
 * Runtime collaborators for {@link StateGoalScheduler}.
 *
 * @property probes - The probe transport (see {@link ProbeSource}).
 * @property onSatisfied - Called once per satisfied probe settle with
 *   the check that passed; the owner dispatches the completion actions
 *   for every entity keyed on it.
 */
export type StateGoalSchedulerDeps = {
  probes: ProbeSource
  onSatisfied: (check: string) => void
}

/** Default rate-limit window between evaluations of one check (ms). */
const DEFAULT_MIN_INTERVAL_MS = 2_000

/** Default retry-tick cadence for still-unsatisfied checks (ms). */
const DEFAULT_RETRY_INTERVAL_MS = 10_000

/**
 * Per-check bookkeeping: when the check last ran, whether a probe is
 * currently in flight, and whether a trigger arrived mid-flight (the
 * coalesce bit).
 *
 * @property lastRunAt - Epoch-ms of the most recent probe launch.
 * @property inFlight - True while a probe for this check is pending.
 * @property dirty - A trigger arrived while in flight; re-run once on
 *   an unsatisfied settle.
 */
type CheckEntry = {
  lastRunAt: number
  inFlight: boolean
  dirty: boolean
}

/**
 * The `{via:'state'}` evaluation policy (see the module header). One
 * instance per `<ChapterProvider/>` mount; `dispose()` on unmount so a
 * late-settling probe can't dispatch into a dead reducer.
 */
export class StateGoalScheduler {
  private readonly probes: ProbeSource
  private readonly onSatisfied: (check: string) => void
  private readonly minIntervalMs: number
  private readonly entries = new Map<string, CheckEntry>()
  /**
   * The latest external trigger's check set, minus satisfied settles —
   * what the retry tick re-evaluates. Replaced wholesale on every
   * {@link evaluate} call so completed goals' checks age out.
   */
  private lastChecks = new Set<string>()
  /** Retry-tick handle; `null` when the tick is disabled. */
  private readonly retryTimer: ReturnType<typeof setInterval> | null
  private disposed = false

  constructor(props: StateGoalSchedulerProps, deps: StateGoalSchedulerDeps) {
    this.probes = deps.probes
    this.onSatisfied = deps.onSatisfied
    this.minIntervalMs = props.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS

    const retryIntervalMs = props.retryIntervalMs ?? DEFAULT_RETRY_INTERVAL_MS
    this.retryTimer =
      retryIntervalMs > 0
        ? setInterval(() => this.retry(), retryIntervalMs)
        : null
    // Node returns a Timeout (unref-able so a mounted scheduler never
    // pins a test process open); browsers return a number — no-op there.
    if (
      this.retryTimer !== null &&
      typeof this.retryTimer === 'object' &&
      'unref' in this.retryTimer
    ) {
      this.retryTimer.unref()
    }
  }

  /**
   * status: the ACTIVE objective's scan window (the not-yet-matched
   * commands a match may resolve — optionals from the pointer up to
   * and including the next required command). Completed / out-of-focus
   * objectives, and commands outside the window, drop out, so a
   * satisfied check stops being probed once the command keyed on it
   * has matched.
   *
   * @param state - The current reducer state.
   * @returns The unique check scripts worth probing right now.
   */
  static collectChecks(state: ChapterState): string[] {
    const checks = new Set<string>()
    const active = state.spec.objectives[state.currentObjectiveIndex]
    if (active) {
      for (const { command } of scanWindow(active, state.currentCommandIndex)) {
        for (const detector of command.satisfiedBy) {
          if (detector.via === 'state') checks.add(detector.check)
        }
      }
    }
    return [...checks]
  }

  /**
   * Evaluate a set of checks, applying the rate-limit / coalesce policy
   * per unique check. Fire-and-forget: probes run in the background and
   * satisfied results surface through `onSatisfied`.
   *
   * @param checks - The currently-relevant check scripts (typically
   *   {@link StateGoalScheduler.collectChecks} over the live state).
   * @param opts - `force: true` bypasses the rate-limit window (the
   *   reassert path); in-flight coalescing still applies.
   */
  evaluate(checks: readonly string[], opts: { force?: boolean } = {}): void {
    if (this.disposed) return
    this.lastChecks = new Set(checks)
    this.evaluateSet(this.lastChecks, opts.force === true)
  }

  /**
   * Stop dispatching: late probe settles are ignored (no `onSatisfied`,
   * no coalesced re-run) and the retry tick stops. Call on provider
   * unmount / spec change.
   */
  dispose(): void {
    this.disposed = true
    if (this.retryTimer !== null) clearInterval(this.retryTimer)
  }

  /**
   * The retry tick: re-run the rate-limit/coalesce loop over the latest
   * trigger's still-unsatisfied checks. Never forces — the per-check
   * rate limit still applies — and never refreshes `lastChecks` (only a
   * real trigger knows the currently-relevant set).
   */
  private retry(): void {
    if (this.disposed || this.lastChecks.size === 0) return
    this.evaluateSet(this.lastChecks, false)
  }

  /** The shared per-check rate-limit/coalesce evaluation loop. */
  private evaluateSet(checks: ReadonlySet<string>, force: boolean): void {
    const now = Date.now()
    for (const check of checks) {
      let entry = this.entries.get(check)
      if (!entry) {
        entry = {
          lastRunAt: Number.NEGATIVE_INFINITY,
          inFlight: false,
          dirty: false,
        }
        this.entries.set(check, entry)
      }
      if (entry.inFlight) {
        entry.dirty = true
        continue
      }
      if (!force && now - entry.lastRunAt < this.minIntervalMs) continue
      this.run(check, entry)
    }
  }

  /** Launch one probe for `check` and wire its settle. */
  private run(check: string, entry: CheckEntry): void {
    entry.lastRunAt = Date.now()
    entry.inFlight = true
    entry.dirty = false
    void this.probes.runCheck(check).then(
      result => this.settle(check, entry, result.satisfied),
      (err: unknown) => {
        // Probe errors are "not satisfied" — never a crash, never a
        // gate (#1149). Debug-only: a flaky wire re-probes on the next
        // trigger without alarming anyone.
        console.debug('[course-kit] state probe failed', { check, err })
        this.settle(check, entry, false)
      },
    )
  }

  /**
   * One probe settled. Satisfied → notify the owner (the dirty bit is
   * moot: everything keyed on the check completes) and drop the check
   * from the retry set. Unsatisfied with a coalesced trigger pending →
   * exactly one follow-up run.
   */
  private settle(check: string, entry: CheckEntry, satisfied: boolean): void {
    entry.inFlight = false
    if (this.disposed) return
    if (satisfied) {
      entry.dirty = false
      this.lastChecks.delete(check)
      this.onSatisfied(check)
      return
    }
    if (entry.dirty) this.run(check, entry)
  }
}
